import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from 'react-query';
import api from '../../services/api';

const TeamSelector = ({ value, onChange, placeholder = "Buscar equipo...", disabled = false, className = "" }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: teamsData, isLoading, error } = useQuery({
    queryKey: ['teams', debouncedSearchTerm],
    queryFn: () => api.get(`/teams?search=${debouncedSearchTerm}&limit=20`).then(res => res.data),
    enabled: isOpen && debouncedSearchTerm.length >= 0,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const teams = teamsData?.teams || [];

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setShowCreateForm(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleTeamSelect = (team) => {
    onChange(team);
    setSearchTerm('');
    setIsOpen(false);
    setShowCreateForm(false);
  };

  const handleCreateNew = () => {
    setShowCreateForm(true);
  };

  const displayValue = value?.name || searchTerm;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        disabled={disabled}
        className={`
          w-full px-3 py-2 border rounded-lg shadow-sm
          focus:ring-2 focus:ring-primary-500 focus:border-primary-500
          dark:bg-dark-700 dark:border-dark-600 dark:text-gray-100
          disabled:bg-gray-50 disabled:text-gray-500 dark:disabled:bg-dark-800 dark:disabled:text-gray-400
          ${value
            ? 'bg-green-50 border-green-300 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300'
            : 'bg-white border-gray-300 text-gray-900 dark:bg-dark-700 dark:border-dark-600 dark:text-gray-100'
          }
        `}
      />

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="p-3 text-center text-gray-500 dark:text-gray-400">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                Buscando equipos...
              </div>
            </div>
          ) : error ? (
            <div className="p-3 text-center text-red-500 dark:text-red-400">
              Error al cargar equipos
            </div>
          ) : teams.length > 0 ? (
            <div>
              {teams.map(team => (
                <button
                  key={team.id}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-dark-700 flex items-center space-x-2 border-b border-gray-100 dark:border-dark-600 last:border-b-0"
                  onClick={() => handleTeamSelect(team)}
                >
                  {team.logo_url && (
                    <img
                      src={team.logo_url}
                      className="w-6 h-6 object-contain"
                      alt=""
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{team.name}</div>
                    {team.short_name && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">{team.short_name}</div>
                    )}
                    {team.league && (
                      <div className="text-xs text-gray-400 dark:text-gray-500">{team.league}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : searchTerm.length > 0 ? (
            <div className="p-3">
              <div className="text-center text-gray-500 dark:text-gray-400 mb-2">
                No se encontraron equipos para "{searchTerm}"
              </div>
              <button
                className="w-full text-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium py-2"
                onClick={handleCreateNew}
              >
                + Crear nuevo equipo "{searchTerm}"
              </button>
            </div>
          ) : (
            <div className="p-3 text-center text-gray-500 dark:text-gray-400">
              Escribe para buscar equipos
            </div>
          )}
        </div>
      )}

      {/* Formulario crear equipo rápido */}
      {showCreateForm && (
        <CreateTeamForm
          initialName={searchTerm}
          onSuccess={(newTeam) => {
            handleTeamSelect(newTeam);
          }}
          onCancel={() => {
            setShowCreateForm(false);
            inputRef.current?.focus();
          }}
        />
      )}
    </div>
  );
};

const CreateTeamForm = ({ initialName, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: initialName || '',
    shortName: '',
    country: 'Colombia',
    league: 'Liga BetPlay'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await api.post('/teams', {
        name: formData.name,
        shortName: formData.shortName || null,
        country: formData.country || null,
        league: formData.league || null
      });

      onSuccess(response.data.team);
    } catch (err) {
      setError(err.response?.data?.message || 'Error creando el equipo');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="absolute z-60 w-full mt-1 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg shadow-xl p-4">
      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Crear nuevo equipo</h3>

      {error && (
        <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre completo *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-dark-600 rounded focus:ring-1 focus:ring-primary-500 dark:bg-dark-700 dark:text-gray-100"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre corto
          </label>
          <input
            type="text"
            value={formData.shortName}
            onChange={(e) => setFormData(prev => ({ ...prev, shortName: e.target.value }))}
            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-dark-600 rounded focus:ring-1 focus:ring-primary-500 dark:bg-dark-700 dark:text-gray-100"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              País
            </label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-dark-600 rounded focus:ring-1 focus:ring-primary-500 dark:bg-dark-700 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Liga
            </label>
            <input
              type="text"
              value={formData.league}
              onChange={(e) => setFormData(prev => ({ ...prev, league: e.target.value }))}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-dark-600 rounded focus:ring-1 focus:ring-primary-500 dark:bg-dark-700 dark:text-gray-100"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !formData.name.trim()}
            className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creando...' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default React.memo(TeamSelector);